import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { DateRangeType } from '@shared/enums/date-range-type.enum';
import { ColorPalette } from '@shared/enums/color-palette.enum';
import { TabModel } from '@shared/model/tab.model';
import { UserType } from '@shared-features/enums/user-type.enum';
import { AccountStatement, AccountStatementCounts } from '@shared-features/models/account-statement.model';
import { AccountStatementStatus } from '@shared-features/enums/account-statement-status.enum';
import { AccountStatementsApiService } from '@shared-features/services/account-statements-api.service';
import { takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { FiltersBaseParams } from '@shared/services/filters-base.service';
import { forkJoin, of } from 'rxjs';
import { AccountStatementType } from '@shared-features/enums/account-statement-source-type.enum';

@Component({
  selector: 'app-account-statements',
  templateUrl: './account-statements.component.html',
  styleUrls: ['./account-statements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountStatementsComponent extends BaseComponent implements OnInit {
  @Input() entityId: number;
  @Input() userType: UserType;

  form: FormGroup = new FormGroup({
    dateRange: new FormControl({ range: DateRangeType.THIS_MONTH, startFrom: null, endAt: null }),
  });
  selectedIndex = 0;
  selectedStatus: AccountStatementStatus = AccountStatementStatus.All;
  currentTabStatus: AccountStatementStatus | null = null;

  dataSource = new MatTableDataSource<AccountStatement>();
  readonly displayedColumns: string[] = [
    'createdAt',
    'referenceId',
    'dueDate',
    'overdueDays',
    'debit',
    'credit',
    'transactionStatus',
  ];

  readonly accountStatementType = AccountStatementType;

  readonly colorPerStatus: Record<Exclude<AccountStatementStatus, AccountStatementStatus.All>, string> = {
    [AccountStatementStatus.Paid]: ColorPalette.Success,
    [AccountStatementStatus.Open]: ColorPalette.Primary,
    [AccountStatementStatus.PartiallyPaid]: ColorPalette.Custom,
    [AccountStatementStatus.Overdue]: ColorPalette.Warn,
  };

  tabs: TabModel[] = Object.values(AccountStatementStatus).map((value) => ({
    label: value,
    showCount: true,
    count: 0,
  }));

  constructor(
    private accountStatementsApi: AccountStatementsApiService,
  ) {
    super();
  }

  ngOnInit(): void {
    const { range: dateRangeType, startFrom, endAt } = this.form.value.dateRange;
    this.filters.params = { dateRangeType, startFrom, endAt, offset: 0, limit: this.pageSize };
    this.form.controls.dateRange.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(({ range: dateRangeType, startFrom, endAt }) => {
      this.filters.params = { ...this.filters.params, dateRangeType, startFrom, endAt, offset: 0 };
      this.loadStatementsAndCounts(true);
    });
    this.loadStatementsAndCounts(true);
  }

  setFilterParams(params: FiltersBaseParams): void {
    this.filters.params = { ...this.filters.params, ...params };
    this.loadStatementsAndCounts(true);
  }

  loadStatementsAndCounts(withCount = false): void {
    if (!this.entityId || !this.userType) return;

    this.load(
      forkJoin({
        statementsResponse: this.accountStatementsApi.getAccountStatements(this.userType, this.entityId, {
          offset: this.filters.params.offset,
          limit: this.filters.params.limit,
          dateRangeType: this.filters.params.dateRangeType as DateRangeType,
          startFrom: this.filters.params.startFrom as string,
          endAt: this.filters.params.endAt as string,
          searchTerm: this.filters.params.searchTerm,
          transactionStatus: this.currentTabStatus,
        }),
        counts: withCount
          ? this.accountStatementsApi.getAccountStatementCounts(this.userType, this.entityId, {
              offset: this.filters.params.offset,
              limit: this.filters.params.limit,
              dateRangeType: this.filters.params.dateRangeType as DateRangeType,
              searchTerm: this.filters.params.searchTerm,
              startFrom: this.filters.params.startFrom as string,
              endAt: this.filters.params.endAt as string,
            })
          : of(null),
      }),
      { isLoadingTransparent: true, emptyContentCheck: (res) => !res?.statementsResponse || !res?.statementsResponse?.length }
    ).subscribe(({ statementsResponse, counts }) => {
      this.dataSource.data = statementsResponse;
      this.totalRowsCount = counts?.totalCount || 0;
      if (counts) {
        this.updateTabsCount(counts);
      }
    });
  }

  private updateTabsCount(counts: AccountStatementCounts): void {
    [
      counts.totalCount,
      counts.openCount,
      counts.partiallyPaidCount,
      counts.paidCount,
      counts.overdueCount,
    ].forEach((count, i) => (this.tabs[i].count = count));
    this.tabs = [...this.tabs];
  }

  onTabIndexChanged(index: number): void {
    if (index !== this.selectedIndex) {
      this.selectedIndex = index;
      this.filters.params = {offset : 0};
      this.viewSelectedTab(index);
    }
  }

  viewSelectedTab(index: number) {
    const label = this.tabs[index]?.label as AccountStatementStatus;
    this.currentTabStatus = label === AccountStatementStatus.All ? null : label;
    this.loadStatementsAndCounts();
  }

  onExportClicked(): void {
    this.load(
      this.accountStatementsApi.getAccountStatements(this.userType, this.entityId, {
        ...this.filters.params,
        transactionStatus: this.currentTabStatus,
        isForExport: true,
      }),
      { isLoadingTransparent: true }
    ).subscribe((res) => {
      window.open((res as any)?.fileUri, '_blank');
      this.showSuccessMessage('SuccessMessages.FileExportedSuccessfully');
    });
  }

}
